/**
 * NavigationMemoryStore
 *
 * In-memory + sessionStorage store for per-surface session state.
 * Remembers tab, section, scroll, filters, sort, pagination, view mode,
 * and selected entity for each surface the user has visited.
 *
 * Design decisions:
 * - In-memory Map is the primary read path (fast).
 * - sessionStorage is the persistence layer (survives SPA soft-nav but not
 *   browser close).
 * - URL-synced state (via useURLState) remains the source of truth for
 *   *durable* state like active tab. This store handles *ephemeral* state
 *   that is too noisy for URLs (scroll offset, view mode, collapsed sections).
 *
 * @module lib/navigation/navigationMemoryStore
 */

import type { SurfaceKey, SurfaceSession } from './types';

// ── Storage key prefix ────────────────────────────────────────────────────
const STORAGE_PREFIX = 'ax1:navmem:';
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 minutes

// ── In-memory cache ───────────────────────────────────────────────────────
const cache = new Map<SurfaceKey, SurfaceSession>();

// ── Helpers ───────────────────────────────────────────────────────────────

function storageKey(key: SurfaceKey): string {
    return `${STORAGE_PREFIX}${key}`;
}

function isExpired(session: SurfaceSession): boolean {
    return Date.now() - session.updatedAt > SESSION_TTL_MS;
}

function writeStorage(key: SurfaceKey, session: SurfaceSession): void {
    try {
        sessionStorage.setItem(storageKey(key), JSON.stringify(session));
    } catch {
        // sessionStorage full or unavailable — degrade gracefully
    }
}

function readStorage(key: SurfaceKey): SurfaceSession | null {
    try {
        const raw = sessionStorage.getItem(storageKey(key));
        if (!raw) return null;
        const session: SurfaceSession = JSON.parse(raw);
        if (isExpired(session)) {
            sessionStorage.removeItem(storageKey(key));
            return null;
        }
        return session;
    } catch {
        return null;
    }
}

// ── Public API ────────────────────────────────────────────────────────────

/**
 * Get the stored session for a surface. Returns null if none exists or
 * the session has expired.
 */
export function getSession(key: SurfaceKey): SurfaceSession | null {
    // Check in-memory first
    const cached = cache.get(key);
    if (cached && !isExpired(cached)) return cached;

    // Fall back to sessionStorage
    const stored = readStorage(key);
    if (stored) {
        cache.set(key, stored);
        return stored;
    }

    // Expired or missing
    cache.delete(key);
    return null;
}

/**
 * Save or update a surface session. Merges with existing session.
 */
export function saveSession(
    key: SurfaceKey,
    partial: Partial<Omit<SurfaceSession, 'surfaceKey' | 'updatedAt'>>,
): SurfaceSession {
    const existing = getSession(key);
    const session: SurfaceSession = {
        surfaceKey: key,
        ...existing,
        ...partial,
        updatedAt: Date.now(),
    };
    cache.set(key, session);
    writeStorage(key, session);
    return session;
}

/**
 * Remove a surface session from memory and storage.
 */
export function clearSession(key: SurfaceKey): void {
    cache.delete(key);
    try {
        sessionStorage.removeItem(storageKey(key));
    } catch { /* ignore */ }
}

/**
 * Clear all navigation memory sessions.
 */
export function clearAllSessions(): void {
    cache.clear();
    try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const k = sessionStorage.key(i);
            if (k?.startsWith(STORAGE_PREFIX)) keysToRemove.push(k);
        }
        keysToRemove.forEach((k) => sessionStorage.removeItem(k));
    } catch { /* ignore */ }
}

/**
 * Get the scroll position for a surface. Convenience shortcut.
 */
export function getScrollTop(key: SurfaceKey): number {
    return getSession(key)?.scrollTop ?? 0;
}

/**
 * Save scroll position for a surface. Convenience shortcut.
 */
export function saveScrollTop(key: SurfaceKey, scrollTop: number): void {
    saveSession(key, { scrollTop });
}
